import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, withCtx as _withCtx, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "p-2 border-b bg-muted/40 text-xs font-bold text-muted-foreground shrink-0 shadow-sm z-10 flex items-center justify-between" }
const _hoisted_2 = { class: "flex items-center gap-3" }
const _hoisted_3 = {
  key: 0,
  class: "flex gap-1"
}
const _hoisted_4 = {
  key: 0,
  class: "text-destructive flex items-center gap-1"
}
const _hoisted_5 = ["innerHTML"]
const _hoisted_6 = {
  key: 1,
  class: "text-muted-foreground flex items-center gap-2"
}
const _hoisted_7 = ["innerHTML"]

import { ref, watch, onMounted, nextTick, computed } from 'vue'
import mermaid from 'mermaid'
import { torcazaMermaidConfig } from '@/config/mermaidTheme'
import { Code2, AlertCircle, Download, Image as ImageIcon, ZoomIn, ZoomOut, Maximize, Expand } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'


export default /*@__PURE__*/_defineComponent({
  __name: 'MermaidLivePreview',
  props: {
    code: {}
  },
  setup(__props: any) {

const props = __props

const svgContent = ref('')
const error = ref('')
const fullscreenContainer = ref<HTMLElement | null>(null)

let renderTimeout: ReturnType<typeof setTimeout> | null = null

const renderMermaid = async () => {
  if (!props.code || !props.code.trim()) {
    svgContent.value = ''
    error.value = ''
    return
  }
  
  let rawCode = props.code
  const match = props.code.match(/```mermaid\n([\s\S]*?)```/)
  if (match) {
    rawCode = match[1]
  } else {
    rawCode = props.code.replace(/```mermaid\n?/g, '').replace(/```\n?/g, '')
  }
  
  try {
    error.value = ''
    const id = `mermaid-render-${Math.random().toString(36).substr(2, 9)}`
    const { svg } = await mermaid.render(id, rawCode)
    svgContent.value = svg
  } catch (e: any) {
    console.error('Mermaid render error', e)
    error.value = e.message || 'Sintaxis de Mermaid inválida'
  }
}

const toggleFullscreen = () => {
  if (!document.fullscreenElement) {
    fullscreenContainer.value?.requestFullscreen().catch(err => {
      console.error(`Error entering fullscreen: ${err.message}`);
    });
  } else {
    document.exitFullscreen();
  }
}

const exportSVG = () => {
  if (!svgContent.value) return
  const blob = new Blob([svgContent.value], { type: 'image/svg+xml;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `diagrama-${Date.now()}.svg`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

const exportPNG = () => {
  if (!svgContent.value) return
  const svg = document.querySelector('.mermaid-container svg') as SVGSVGElement
  if (!svg) return
  
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  
  const svgClone = svg.cloneNode(true) as SVGSVGElement
  const bbox = svg.getBoundingClientRect()
  svgClone.setAttribute('width', `${bbox.width}`)
  svgClone.setAttribute('height', `${bbox.height}`)
  
  const svgData = new XMLSerializer().serializeToString(svgClone)
  
  // Use base64 data URL instead of Blob to avoid Tainted Canvas SecurityError
  const base64SVG = window.btoa(unescape(encodeURIComponent(svgData)))
  const url = `data:image/svg+xml;base64,${base64SVG}`
  
  const img = new Image()
  img.crossOrigin = 'anonymous'
  
  img.onload = () => {
    canvas.width = bbox.width * 2 // Escala x2 para alta resolución
    canvas.height = bbox.height * 2
    
    if (ctx) {
      ctx.fillStyle = 'white'
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
      
      try {
        const pngUrl = canvas.toDataURL('image/png')
        const a = document.createElement('a')
        a.href = pngUrl
        a.download = `diagrama-${Date.now()}.png`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      } catch (err) {
        console.error('Error al exportar PNG (posible Canvas Tainted):', err)
        error.value = 'No se pudo exportar a PNG por restricciones de seguridad del navegador. Intenta exportar como SVG.'
      }
    }
  }
  
  img.src = url
}

watch(() => props.code, () => {
  if (renderTimeout) clearTimeout(renderTimeout)
  renderTimeout = setTimeout(() => {
    renderMermaid()
  }, 400)
}, { immediate: true })

// --- PAN AND ZOOM LOGIC ---
const scale = ref(1)
const translateX = ref(0)
const translateY = ref(0)
let isDragging = false
let startX = 0
let startY = 0

const onWheel = (e: WheelEvent) => {
  if (!svgContent.value) return
  const zoomIntensity = 0.1
  const delta = e.deltaY > 0 ? -zoomIntensity : zoomIntensity
  scale.value = Math.min(Math.max(0.1, scale.value + delta), 5)
}

const onMouseDown = (e: MouseEvent) => {
  if (!svgContent.value) return
  isDragging = true
  startX = e.clientX - translateX.value
  startY = e.clientY - translateY.value
}

const onMouseMove = (e: MouseEvent) => {
  if (!isDragging) return
  translateX.value = e.clientX - startX
  translateY.value = e.clientY - startY
}

const onMouseUp = () => {
  isDragging = false
}

const zoomIn = () => scale.value = Math.min(scale.value + 0.2, 5)
const zoomOut = () => scale.value = Math.max(scale.value - 0.2, 0.1)
const resetZoom = () => {
  scale.value = 1
  translateX.value = 0
  translateY.value = 0
}

const containerStyle = computed(() => ({
  transform: `translate(${translateX.value}px, ${translateY.value}px) scale(${scale.value})`,
  transformOrigin: 'center center',
  width: '100%',
  height: '100%',
  transition: isDragging ? 'none' : 'transform 0.1s ease-out'
}))

onMounted(() => {
  mermaid.initialize(torcazaMermaidConfig)
  nextTick(() => {
    renderMermaid()
  })
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "relative w-full h-full flex flex-col bg-slate-50 dark:bg-slate-900 overflow-hidden",
    ref_key: "fullscreenContainer",
    ref: fullscreenContainer
  }, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _cache[2] || (_cache[2] = _createElementVNode("span", null, "Previsualización", -1)),
        (svgContent.value && !error.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
              _createVNode(_unref(Button), {
                size: "icon",
                variant: "ghost",
                class: "h-6 w-6 text-muted-foreground hover:text-primary",
                onClick: toggleFullscreen,
                title: "Pantalla Completa (Presentación)"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(Expand), { class: "w-3 h-3" })
                ]),
                _: 1
              }),
              _createVNode(_unref(Button), {
                size: "icon",
                variant: "ghost",
                class: "h-6 w-6 text-muted-foreground hover:text-primary",
                onClick: zoomIn,
                title: "Acercar"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(ZoomIn), { class: "w-3 h-3" })
                ]),
                _: 1
              }),
              _createVNode(_unref(Button), {
                size: "icon",
                variant: "ghost",
                class: "h-6 w-6 text-muted-foreground hover:text-primary",
                onClick: zoomOut,
                title: "Alejar"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(ZoomOut), { class: "w-3 h-3" })
                ]),
                _: 1
              }),
              _createVNode(_unref(Button), {
                size: "icon",
                variant: "ghost",
                class: "h-6 w-6 text-muted-foreground hover:text-primary mr-2",
                onClick: resetZoom,
                title: "Restaurar vista"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(Maximize), { class: "w-3 h-3" })
                ]),
                _: 1
              }),
              _createVNode(_unref(Button), {
                size: "sm",
                variant: "outline",
                class: "h-6 text-[10px] px-2 py-0 border-primary/20 hover:border-primary",
                onClick: exportSVG
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(Download), { class: "w-3 h-3 mr-1" }),
                  _cache[0] || (_cache[0] = _createTextVNode(" SVG ", -1))
                ]),
                _: 1
              }),
              _createVNode(_unref(Button), {
                size: "sm",
                variant: "outline",
                class: "h-6 text-[10px] px-2 py-0 border-primary/20 hover:border-primary",
                onClick: exportPNG
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(ImageIcon), { class: "w-3 h-3 mr-1" }),
                  _cache[1] || (_cache[1] = _createTextVNode(" PNG ", -1))
                ]),
                _: 1
              })
            ]))
          : _createCommentVNode("", true)
      ]),
      (error.value)
        ? (_openBlock(), _createElementBlock("span", _hoisted_4, [
            _createVNode(_unref(AlertCircle), { class: "w-3 h-3" }),
            _cache[3] || (_cache[3] = _createTextVNode(" Error de sintaxis", -1))
          ]))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", {
      class: "flex-1 overflow-hidden p-4 flex items-center justify-center relative cursor-grab active:cursor-grabbing",
      onWheel: _withModifiers(onWheel, ["prevent"]),
      onMousedown: onMouseDown,
      onMousemove: onMouseMove,
      onMouseup: onMouseUp,
      onMouseleave: onMouseUp
    }, [
      (svgContent.value && !error.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            innerHTML: svgContent.value,
            class: "max-w-full max-h-full flex items-center justify-center mermaid-container",
            style: _normalizeStyle(containerStyle.value)
          }, null, 12, _hoisted_5))
        : (!svgContent.value && !error.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
              _createVNode(_unref(Code2), { class: "w-8 h-8 opacity-20" }),
              _cache[4] || (_cache[4] = _createElementVNode("span", { class: "opacity-50 text-sm" }, "Esperando código...", -1))
            ]))
          : _createCommentVNode("", true),
      (error.value && svgContent.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 2,
            innerHTML: svgContent.value,
            class: "max-w-full max-h-full flex items-center justify-center mermaid-container opacity-30 grayscale",
            style: _normalizeStyle(containerStyle.value)
          }, null, 12, _hoisted_7))
        : _createCommentVNode("", true)
    ], 32)
  ], 512))
}
}

})